import React, { useState } from 'react';
import {
    Shield,
    Eye,
    Lock,
    Users,
    Database,
    Globe,
    Mail,
    Phone,
    FileText,
    Calendar,
    ChevronDown,
    ChevronUp,
    Home,
    ArrowLeft
} from 'lucide-react';

const PrivacyPolicy = () => {
    const [expandedSection, setExpandedSection] = useState(null);

    const toggleSection = (sectionId) => {
        setExpandedSection(expandedSection === sectionId ? null : sectionId);
    };

    const lastUpdated = "June 18, 2025";

    const sections = [
        {
            id: 'information-collection',
            title: 'Information We Collect',
            icon: Database,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Personal Information</h4>
                    <p className="text-soil-600">
                        When you create an account or use our services, we may collect:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-soil-600">
                        <li>Name, email address, and phone number</li>
                        <li>Profile photo and personal preferences</li>
                        <li>Identity verification documents (ID, passport)</li>
                        <li>Employment and income information</li>
                        <li>Rental history and references</li>
                    </ul>

                    <h4 className="font-semibold text-soil-900 mt-6">Property Information</h4>
                    <p className="text-soil-600">
                        For property managers and landlords:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-soil-600">
                        <li>Property details, photos, and descriptions</li>
                        <li>Rental terms and pricing information</li>
                        <li>Property ownership verification documents</li>
                        <li>Bank account details for payments</li>
                    </ul>

                    <h4 className="font-semibold text-soil-900 mt-6">Usage Data</h4>
                    <ul className="list-disc pl-6 space-y-1 text-soil-600">
                        <li>Search queries and property views</li>
                        <li>Communication through our platform</li>
                        <li>Device information and IP addresses</li>
                        <li>Cookies and similar tracking technologies</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'information-use',
            title: 'How We Use Your Information',
            icon: Eye,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        We use the information we collect to:
                    </p>
                    <div className="grid gap-4">
                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">Provide Our Services</h4>
                            <ul className="list-disc pl-6 space-y-1 text-highland-600">
                                <li>Create and manage your account</li>
                                <li>Process rental applications and payments</li>
                                <li>Facilitate communication between users</li>
                                <li>Verify identity and conduct background checks</li>
                            </ul>
                        </div>

                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">Improve User Experience</h4>
                            <ul className="list-disc pl-6 space-y-1 text-dawn-600">
                                <li>Personalize property recommendations</li>
                                <li>Send relevant notifications and updates</li>
                                <li>Analyze usage patterns to improve our platform</li>
                                <li>Provide customer support</li>
                            </ul>
                        </div>

                        <div className="bg-jacaranda-100 p-4 rounded-lg">
                            <h4 className="font-semibold text-jacaranda-600 mb-2">Legal and Safety</h4>
                            <ul className="list-disc pl-6 space-y-1 text-jacaranda-600">
                                <li>Comply with legal obligations</li>
                                <li>Prevent fraud and ensure platform security</li>
                                <li>Resolve disputes and enforce our terms</li>
                                <li>Protect the rights and safety of our users</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'information-sharing',
            title: 'Information Sharing and Disclosure',
            icon: Users,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        We may share your information in the following circumstances:
                    </p>

                    <div className="space-y-4">
                        <div className="border-l-4 border-highland-500 pl-4">
                            <h4 className="font-semibold text-soil-900">With Other Users</h4>
                            <p className="text-soil-600">
                                When you apply for a property, your relevant information is shared with the property manager.
                                When you list a property, your contact information is shared with interested tenants.
                            </p>
                        </div>

                        <div className="border-l-4 border-dawn-500 pl-4">
                            <h4 className="font-semibold text-soil-900">Service Providers</h4>
                            <p className="text-soil-600">
                                We work with trusted third-party providers for payment processing, background checks,
                                identity verification, and other essential services.
                            </p>
                        </div>

                        <div className="border-l-4 border-jacaranda-500 pl-4">
                            <h4 className="font-semibold text-soil-900">Legal Requirements</h4>
                            <p className="text-soil-600">
                                We may disclose information when required by law, court order, or government request,
                                or to protect the rights and safety of our users and the public.
                            </p>
                        </div>

                        <div className="border-l-4 border-murram-500 pl-4">
                            <h4 className="font-semibold text-soil-900">Business Transfers</h4>
                            <p className="text-soil-600">
                                In the event of a merger, acquisition, or sale of assets, user information may be
                                transferred to the new entity, subject to this privacy policy.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'data-security',
            title: 'Data Security',
            icon: Lock,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        We implement comprehensive security measures to protect your personal information:
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-soil-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-soil-900 mb-2 flex items-center">
                                <Shield className="w-5 h-5 mr-2 text-highland-500" />
                                Technical Safeguards
                            </h4>
                            <ul className="text-sm text-soil-600 space-y-1">
                                <li>• SSL/TLS encryption for data transmission</li>
                                <li>• Encrypted data storage</li>
                                <li>• Regular security audits and updates</li>
                                <li>• Secure server infrastructure</li>
                            </ul>
                        </div>

                        <div className="bg-soil-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-soil-900 mb-2 flex items-center">
                                <Users className="w-5 h-5 mr-2 text-highland-500" />
                                Access Controls
                            </h4>
                            <ul className="text-sm text-soil-600 space-y-1">
                                <li>• Limited employee access on need-to-know basis</li>
                                <li>• Multi-factor authentication</li>
                                <li>• Regular access reviews</li>
                                <li>• Employee security training</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-dawn-100/40 p-4 rounded-lg border border-dawn-100">
                        <h4 className="font-semibold text-dawn-600 mb-2">Important Note</h4>
                        <p className="text-dawn-600 text-sm">
                            While we implement industry-standard security measures, no method of transmission over the internet
                            or electronic storage is 100% secure. We encourage users to use strong passwords and keep their
                            account information confidential.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'user-rights',
            title: 'Your Rights and Choices',
            icon: FileText,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        As a user of Rent Utopia, you have the following rights regarding your personal information:
                    </p>

                    <div className="space-y-3">
                        <div className="flex items-start space-x-3 p-3 bg-highland-50 rounded-lg">
                            <Eye className="w-5 h-5 text-highland-500 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-highland-700">Access and Portability</h4>
                                <p className="text-highland-600 text-sm">
                                    Request a copy of your personal data and download your information in a portable format.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 bg-dawn-100/40 rounded-lg">
                            <FileText className="w-5 h-5 text-dawn-600 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-dawn-600">Correction and Updates</h4>
                                <p className="text-dawn-600 text-sm">
                                    Update or correct your personal information through your account settings or by contacting us.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 bg-murram-100/40 rounded-lg">
                            <Database className="w-5 h-5 text-murram-500 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-murram-600">Deletion</h4>
                                <p className="text-murram-600 text-sm">
                                    Request deletion of your account and personal data, subject to legal retention requirements.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 bg-jacaranda-100 rounded-lg">
                            <Mail className="w-5 h-5 text-jacaranda-500 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-jacaranda-600">Communication Preferences</h4>
                                <p className="text-jacaranda-600 text-sm">
                                    Manage your email and notification preferences in your account settings.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-soil-100 p-4 rounded-lg">
                        <h4 className="font-semibold text-soil-900 mb-2">How to Exercise Your Rights</h4>
                        <p className="text-soil-600 text-sm mb-2">
                            To exercise any of these rights, you can:
                        </p>
                        <ul className="text-sm text-soil-600 space-y-1">
                            <li>• Log into your account and update your settings</li>
                            <li>• Contact our support team at itorama254@gmail.com</li>
                            <li>• Call our privacy hotline at +254 712 292 899</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'cookies',
            title: 'Cookies and Tracking',
            icon: Globe,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        We use cookies and similar technologies to enhance your experience on our platform:
                    </p>

                    <div className="space-y-3">
                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">Essential Cookies</h4>
                            <p className="text-highland-600 text-sm">
                                Required for the website to function properly, including login sessions and security features.
                            </p>
                        </div>

                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">Performance Cookies</h4>
                            <p className="text-dawn-600 text-sm">
                                Help us understand how users interact with our website to improve performance and user experience.
                            </p>
                        </div>

                        <div className="bg-jacaranda-100 p-4 rounded-lg">
                            <h4 className="font-semibold text-jacaranda-600 mb-2">Functional Cookies</h4>
                            <p className="text-jacaranda-600 text-sm">
                                Remember your preferences and settings to provide a personalized experience.
                            </p>
                        </div>

                        <div className="bg-murram-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-murram-600 mb-2">Marketing Cookies</h4>
                            <p className="text-murram-600 text-sm">
                                Used to deliver relevant advertisements and track the effectiveness of our marketing campaigns.
                            </p>
                        </div>
                    </div>

                    <div className="bg-soil-100 p-4 rounded-lg">
                        <h4 className="font-semibold text-soil-900 mb-2">Managing Cookies</h4>
                        <p className="text-soil-600 text-sm">
                            You can control cookies through your browser settings. Please note that disabling certain
                            cookies may limit the functionality of our website.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    const CollapsibleSection = ({ section }) => {
        const Icon = section.icon;
        const isExpanded = expandedSection === section.id;

        return (
            <div className="border border-soil-100 rounded-lg overflow-hidden">
                <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full px-6 py-4 text-left bg-white hover:bg-soil-50 transition-colors duration-200 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5 text-highland-500" />
                        <h3 className="text-lg font-semibold text-soil-900">{section.title}</h3>
                    </div>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-soil-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-soil-400" />
                    )}
                </button>

                {isExpanded && (
                    <div className="px-6 py-4 bg-soil-50 border-t border-soil-100">
                        {section.content}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-karibu-100">
            {/* Header */}
            <div className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="p-3 bg-highland-50 rounded-full">
                                <Shield className="w-8 h-8 text-highland-500" />
                            </div>
                        </div>
                        <h1 className="font-display text-3xl font-bold text-soil-900 mb-2">Privacy Policy</h1>
                        <p className="text-soil-600 max-w-2xl mx-auto">
                            At Rent Utopia, we're committed to protecting your privacy and being transparent about how we collect,
                            use, and share your information. This policy explains our data practices in detail.
                        </p>
                        <div className="flex items-center justify-center mt-4 text-sm text-soil-400">
                            <Calendar className="w-4 h-4 mr-1" />
                            <span>Last updated: {lastUpdated}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <Home className="w-4 h-4 mr-1" />
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">Privacy Policy</span>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                {/* Introduction */}
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                    <h2 className="text-xl font-semibold text-soil-900 mb-4">Introduction</h2>
                    <p className="text-soil-600 mb-4">
                        Rent Utopia operates a digital platform that connects property seekers with property managers and landlords
                        in Kenya. This privacy policy describes how we collect, use, process, and disclose your information when
                        you use our website, mobile applications, and related services.
                    </p>
                    <p className="text-soil-600">
                        By using our services, you agree to the collection and use of information in accordance with this policy.
                        We will not use or share your information with anyone except as described in this privacy policy.
                    </p>
                </div>

                {/* Collapsible Sections */}
                <div className="space-y-4 mb-8">
                    {sections.map((section) => (
                        <CollapsibleSection key={section.id} section={section} />
                    ))}
                </div>

            </div>
        </div>
    );
};

export default PrivacyPolicy;